/**
 * EnrollmentsTab Component
 *
 * A tab component that displays a student's course enrollments with
 * search and filtering capabilities. It fetches enrollment data using
 * React Query and presents the results in an expandable accordion layout.
 *
 * ## Features:
 * - **Data Fetching**: Retrieves enrollments for a given student via React Query.
 * - **Search with Debounce**: Filters enrollments by section title or abbreviation
 *   with a debounced input to improve performance.
 * - **Accordion Layout**: Organizes enrollments within a collapsible section.
 * - **Loading State**: Displays a spinner while data is being fetched.
 * - **Empty State Handling**: Shows a default alert when no enrollments are found.
 * - **Alternating Row Styles**: Improves readability with striped row backgrounds.
 *
 * ## Props:
 * - `userId` (string): The ID of the student whose enrollments should be fetched.
 *
 * ## Behavior:
 * - Fetches up to 1000 enrollments for the given `userId`.
 * - Filters enrollments based on user input (case-insensitive match on:
 *   - section title
 *   - section abbreviation).
 * - Applies a 300ms debounce to search input to reduce unnecessary filtering.
 * - Displays:
 *   - Loading spinner while fetching
 *   - Alert if no results
 *   - List of enrollments if available
 *
 * ## Example Usage:
 * ```tsx
 * <EnrollmentsTab userId={selectedRow?.userId} />
 * ```
 *
 * @param {string} userId - The unique identifier of the student.
 */

import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ModalDefaultAlert } from '../../../../components/ModalDefaultAlert'
import { black, grey, shade } from '../../../../colors'
import { useQuery } from '@tanstack/react-query'
import { getEnrollmentsOfStudent } from '../../../../APIManager/UserManager'
import Search from '../../../../components/Search'

interface EnrollmentsTabProps {
  userId: string
}

// Ensures proper text truncation
const noWrapTextProps = { textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }

export const EnrollmentsTab: React.FC<EnrollmentsTabProps> = ({ userId }) => {
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState(search)

  // Delay search input to prevent excessive filtering while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)

    return () => { window.clearTimeout(timer) }
  }, [search])

  // Get all enrollments of the student
  const { data: enrollments, isLoading } = useQuery({
    queryKey: ['enrollments', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await getEnrollmentsOfStudent(
        userId, { pageSize: 1000 }
      )
      return data
    }
  })

  // Memoize filtered enrollments based on search input
  const filteredEnrollments = useMemo(() => {
    const formattedSearch = debouncedSearch.trim().toLowerCase()

    const filtered = enrollments?.filter((enrollment: any) => {
      if (!formattedSearch) return true

      const title = enrollment.section?.title?.toLowerCase() ?? ''
      const abbreviation = enrollment.section?.abbreviation?.toLowerCase() ?? ''
      return title.includes(formattedSearch) || abbreviation.includes(formattedSearch)
    })

    return filtered?.map((enrollment: any, idx: number) => (
      <Box
        key={idx}
        bgcolor={idx % 2 === 0 ? 'transparent' : shade[1]}
        p={theme.spacing(6)}
        display="flex"
        flexDirection="row"
        justifyContent='space-between'
        alignItems='center'
        gap={theme.spacing(2)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2), minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, ...noWrapTextProps }}>
            {enrollment.section.abbreviation}
          </Typography>
          <Typography sx={{ color: grey[150], ...noWrapTextProps }}>
            {enrollment.section.title}
          </Typography>
        </Box>
        <Typography sx={{ minWidth: '40%', textAlign: 'right', color: grey[150], ...noWrapTextProps }}>
          {enrollment.term.termName}
        </Typography>
      </Box>
    ))
  }, [enrollments, debouncedSearch])

  const handleSearch = (value: string): void => {
    setSearch(value)
  }

  return (
    <Box display="flex" flexDirection="column" mt={2} gap={theme.spacing(6)}>
      {/* Search Enrollments */}
      <Search
        placeholder='Search for Course Sections'
        onSearch={handleSearch}
        fillContainer={true}
        onChange={handleSearch}
        showClearIcon={false}
      />

      {/* Enrollments */}
      <Accordion
        defaultExpanded
        elevation={0}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ fontWeight: 600, color: black[100] }}
        >
          Enrollments ({filteredEnrollments?.length ?? 0})
        </AccordionSummary>
        <AccordionDetails sx={{ p: theme.spacing(1) }}>
          {isLoading && (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight={theme.spacing(20)}>
              <CircularProgress thickness={4} value={2}/>
            </Box>
          )}
          {!isLoading && filteredEnrollments?.length === 0 &&
            <ModalDefaultAlert message="No enrollments found for this student." />
          }
          {!isLoading && filteredEnrollments?.length > 0 &&
            filteredEnrollments
          }
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
